import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  Checkbox,
  Typography,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FILE_TYPE } from "constants/fileTypes";
import { SPINNER_SIZE } from "constants/general";
import { BrandButton } from "components/brand";
import { ADMIN_TEXTS } from "constants/adminTexts";
import "./ItemsSelectModal.scss";

const imageBase = process.env.REACT_APP_API_IMAGE_URL;

const ItemsSelectModal = ({
  open,
  onClose,
  onSave,
  statusField,
  selectedIds,
  saving = false,
  title = ADMIN_TEXTS.common.selectItems,
}) => {
  const products = useSelector((state) => state.products.allProducts);
  const [selected, setSelected] = useState({});

  const handleToggle = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSave = () => {
    onSave(products.filter((p) => selected[p.id]));
  };

  const getImageUrl = (image) => `${imageBase}${image.path}`;

  useEffect(() => {
    if (!open) return;
    const initial = {};
    if (selectedIds) {
      selectedIds.forEach((id) => {
        initial[id] = true;
      });
    } else {
      products.forEach((p) => {
        if (statusField && p[statusField]) {
          initial[p.id] = true;
        }
      });
    }
    setSelected(initial);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="modal-dialog-title">
        {title}
        <IconButton
          onClick={onClose}
          disabled={saving}
          className="close-icon-button"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <List disablePadding className="modal-list">
          {products.map((product) => (
            <ListItem
              key={product.id}
              onClick={() => handleToggle(product.id)}
              className="modal-list-item"
            >
              <Box className="product-thumbnail">
                {product.image &&
                  (product.image.type === FILE_TYPE.VIDEO ? (
                    <video
                      src={getImageUrl(product.image)}
                      className="media-cover"
                    />
                  ) : (
                    <img
                      src={getImageUrl(product.image)}
                      alt={product.title}
                      className="media-cover"
                    />
                  ))}
              </Box>

              <Typography variant="body1" className="product-name" noWrap>
                {product.title}
              </Typography>

              <Checkbox
                checked={!!selected[product.id]}
                onChange={() => handleToggle(product.id)}
                onClick={(e) => e.stopPropagation()}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions>
        <BrandButton
          variant="secondary"
          onClick={onClose}
          color="inherit"
          disabled={saving}
        >
          {ADMIN_TEXTS.common.cancel}
        </BrandButton>
        <BrandButton onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? (
            <CircularProgress size={SPINNER_SIZE} color="inherit" />
          ) : (
            ADMIN_TEXTS.common.save
          )}
        </BrandButton>
      </DialogActions>
    </Dialog>
  );
};

export default ItemsSelectModal;
